import React from "react";
import { useAuth } from "./AuthContext";

// Local API_BASE (same logic style as App.jsx)
const API_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_BACKEND_URL &&
    String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== "undefined" &&
    String(window.location.hostname).indexOf(
      "ai-translation-frontend-sandbox"
    ) !== -1)
    ? "https://ai-translation-backend-sandbox.onrender.com"
    : "https://ai-translation-backend.onrender.com");

/** File types: keep consistent with WorkflowBuilder */
const OTHER_LABEL =
  "Other (requires customization, additional charges may apply)";
const FILE_TYPES = [
  "DOCX (Microsoft Word)",
  "XLSX (Microsoft Excel)",
  "PPTX (Microsoft PowerPoint)",
  "IDML (Adobe InDesign)",
  "HTML",
  "XML",
  "JSON",
  "YML",
  "XLIFF",
  "SDLXLIFF (Trados)",
  "PDF (limited support)",
  OTHER_LABEL,
];

const FILE_TYPE_TEST_IDS = {
  "DOCX (Microsoft Word)": "custom-workflow-file-type-docx",
  "XLSX (Microsoft Excel)": "custom-workflow-file-type-xlsx",
  "PPTX (Microsoft PowerPoint)": "custom-workflow-file-type-pptx",
  "IDML (Adobe InDesign)": "custom-workflow-file-type-idml",
  HTML: "custom-workflow-file-type-html",
  XML: "custom-workflow-file-type-xml",
  JSON: "custom-workflow-file-type-json",
  YML: "custom-workflow-file-type-yml",
  XLIFF: "custom-workflow-file-type-xliff",
  "SDLXLIFF (Trados)": "custom-workflow-file-type-sdlxliff",
  "PDF (limited support)": "custom-workflow-file-type-pdf",
  [OTHER_LABEL]: "custom-workflow-file-type-other",
};

// Map from legacy internal keys -> labels, for older drafts (sandbox safety)
const FILE_KEY_TO_LABEL = {
  docx: "DOCX (Microsoft Word)",
  xlsx: "XLSX (Microsoft Excel)",
  pptx: "PPTX (Microsoft PowerPoint)",
  idml: "IDML (Adobe InDesign)",
  html: "HTML",
  xml: "XML",
  json: "JSON",
  yml: "YML",
  yaml: "YML",
  xliff: "XLIFF",
  xlf: "XLIFF",
  sdlxliff: "SDLXLIFF",
  pdf: "PDF (limited support)",
};

/** PEMT options – same as WorkflowBuilder */
const PEMT_OPTIONS = [
  {
    key: "full_review",
    label: "Full Human Review (additional charges apply)",
  },
  {
    key: "term_egregious",
    label:
      "Limited Review (terminology verification + egregious errors, additional charges apply)",
  },
  { key: "none", label: "No Review" },
];

/** Post-translation formatting – same as WorkflowBuilder */
const PT_FORMATTING = [
  {
    key: "pro_match_en",
    label:
      "Professional File Formatting (matches English formatting, additional charges apply)",
  },
  {
    key: "none",
    label: "Automated File Formatting Only (limited, may contain errors)",
  },
];

/**
 * Custom Workflow Editor (post-wizard)
 *
 * Shows the current workflowdraft in a single page and allows
 * the user to tweak key settings without re-running the wizard.
 */
export default function CustomWorkflow({ onBack, onOpenTerminologySettings, onOpenTMXSettings, onDirtyChange }) {
  const { jwt, user, setUserAndTick } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [loadError, setLoadError] = React.useState("");
  const [saveError, setSaveError] = React.useState("");
  const [saveMsg, setSaveMsg] = React.useState("");
  // Mirror workflow mode locally so toggles don’t force a page reload
  const [localUsesCustom, setLocalUsesCustom] = React.useState(!!(user?.uses_custom_workflow));

  // Language pair + language name mapping
  const [langCodeMap, setLangCodeMap] = React.useState({});
  const [langNameToCode, setLangNameToCode] = React.useState({});
  const languageList = React.useMemo(
    () => Object.keys(langNameToCode).sort((a, b) => a.localeCompare(b)),
    [langNameToCode]
  );

  // Workflow settings
  const [pairs, setPairs] = React.useState([]);
  const [newSource, setNewSource] = React.useState("");
  const [newTarget, setNewTarget] = React.useState("");

  const [fileTypes, setFileTypes] = React.useState([]); // array of labels from FILE_TYPES
  const [otherFileType, setOtherFileType] = React.useState("");
  const [otherSample, setOtherSample] = React.useState({
    file: null,
    filename: "",
  });

  const [terminologyEnabled, setTerminologyEnabled] = React.useState(false);
  const [pemt, setPemt] = React.useState("");
  const [formatting, setFormatting] = React.useState("");


  // Terminology / TMX entries (for summary display on this screen)
  const [terminologyEntries, setTerminologyEntries] = React.useState([]);
  const [terminologyLoading, setTerminologyLoading] = React.useState(false);
  const [tmxEntries, setTMXEntries] = React.useState([]);
  const [tmxLoading, setTMXLoading] = React.useState(false);


  // Track whether there are unsaved changes in the custom workflow editor
  const [hasChanges, setHasChanges] = React.useState(false);
  
  React.useEffect(() => {
    if (typeof onDirtyChange === "function") {
      onDirtyChange(hasChanges);
    }
  }, [hasChanges, onDirtyChange]);
  
  const fileInputRef = React.useRef(null);

  // Normalize a stored language value (code or name) into a display name
  const normalizeLangValue = React.useCallback(
    (value) => {
      if (!value) return "";
      // Already a known display name?
      if (langNameToCode[value]) return value;
      // Direct code mapping
      if (langCodeMap[value]) return langCodeMap[value];
      // Try loose matching on code-style strings
      const lc = String(value).toLowerCase().replace(/_/g, "-");
      for (const [code, name] of Object.entries(langCodeMap || {})) {
        if (code.toLowerCase().replace(/_/g, "-") === lc) {
          return name;
        }
      }
      return value;
    },
    [langCodeMap, langNameToCode]
  );


  // Helper: display a consistent language name from a code or name
  const displayLang = React.useCallback(
    (codeOrName) => normalizeLangValue(codeOrName || ""),
    [normalizeLangValue]
  );

  const loadTerminologyEntries = React.useCallback(async () => {
    if (!jwt) return;
    setTerminologyLoading(true);
    try {
      const res = await fetch(`${API_BASE}/terminology`, {
        headers: { Authorization: `Bearer ${jwt}` },
        credentials: "include",
      });
      const data = await res.json().catch(() => []);
      const rows = Array.isArray(data) ? data : [];
      setTerminologyEntries(rows);
    } catch (err) {
      console.error("Failed to load terminology entries in CustomWorkflow:", err);
      setTerminologyEntries([]);
    } finally {
      setTerminologyLoading(false);
    }
  }, [jwt]);

  const loadTMXEntries = React.useCallback(async () => {
    if (!jwt) return;
    setTMXLoading(true);
    try {
      const res = await fetch(`${API_BASE}/tmx`, {
        headers: { Authorization: `Bearer ${jwt}` },
        credentials: "include",
      });
      const data = await res.json().catch(() => []);
      const rows = Array.isArray(data) ? data : [];
      setTMXEntries(rows);
    } catch (err) {
      console.error("Failed to load TMX entries in CustomWorkflow:", err);
      setTMXEntries([]);
    } finally {
      setTMXLoading(false);
    }
  }, [jwt]);


  // Language codes -> names mapping from backend
  React.useEffect(() => {
    let cancelled = false;
    async function fetchLangCodes() {
      try {
        const resp = await fetch(`${API_BASE}/lang_codes`);
        const data = await resp.json();
        if (cancelled) return;
        setLangCodeMap(data.code_to_name || {});
        setLangNameToCode(data.name_to_code || {});
      } catch (err) {
        // Non-fatal; just log for diagnostics
        console.error("Failed to load language codes in CustomWorkflow:", err);
      }
    }
    fetchLangCodes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the current draft for the logged-in user
  React.useEffect(() => {
    let cancelled = false;

    async function loadDraft() {
      setLoading(true);
	  setLoadError("");
      setSaveMsg("");
      try {
        const res = await fetch(`${API_BASE}/workflow/draft/me`, {
          headers: jwt
            ? {
                Authorization: `Bearer ${jwt}`,
              }
            : {},
          credentials: "include",
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const detail =
            data?.detail || `Failed to load workflow (${res.status})`;
          throw new Error(detail);
        }
        const data = await res.json();

        if (cancelled) return;

        // pairs_json -> structured array of {source, targets[]} OR {source,target}
        if (data.pairs_json) {
          try {
            const parsed = JSON.parse(data.pairs_json);
            const flattened = [];
            if (Array.isArray(parsed)) {
              parsed.forEach((entry) => {
                const src = entry.source || entry.src || "";
                if (Array.isArray(entry.targets) && entry.targets.length) {
                  entry.targets.forEach((t) => {
                    flattened.push({
                      source: src || "",
                      target: t || "",
                    });
                  });
                } else if (entry.target) {
                  flattened.push({
                    source: src || "",
                    target: entry.target || "",
                  });
                }
              });
            }
            setPairs(flattened.length ? flattened : []);
          } catch (e) {
            console.error("Failed to parse pairs_json:", e);
            setPairs([]);
          }
        } else {
          setPairs([]);
        }

        // file_types_json -> array of labels (or legacy keys or objects)
        if (data.file_types_json) {
          try {
            const ft = JSON.parse(data.file_types_json);
            if (Array.isArray(ft)) {
              const labels = [];
              ft.forEach((val) => {
                if (!val && val !== 0) return;
                if (typeof val === "string") {
                  const str = val;
                  if (FILE_TYPES.includes(str)) {
                    labels.push(str);
                  } else if (FILE_KEY_TO_LABEL[str]) {
                    labels.push(FILE_KEY_TO_LABEL[str]);
                  }
                } else if (typeof val === "object") {
                  const lbl = val.label;
                  const key = val.key;
                  if (lbl && FILE_TYPES.includes(lbl)) {
                    labels.push(lbl);
                  } else if (key && FILE_KEY_TO_LABEL[key]) {
                    labels.push(FILE_KEY_TO_LABEL[key]);
                  }
                }
              });
              setFileTypes(labels);
            } else {
              setFileTypes([]);
            }
          } catch (e) {
            console.error("Failed to parse file_types_json:", e);
            setFileTypes([]);
          }
        } else {
          setFileTypes([]);
        }

        setOtherFileType(data.other_file_type || "");
        setOtherSample({
          file: null,
          filename: data.other_sample_filename || "",
        });

        setTerminologyEnabled(!!data.terminology_enabled);
        setPemt(data.pemt || "");
        setFormatting(data.formatting || "");

        // Draft is now loaded; no unsaved changes yet
        setHasChanges(false);
		
      } catch (e) {
        if (!cancelled) {
          setLoadError(String(e.message || e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (jwt) {
      loadDraft();
    } else {
      setLoading(false);
      setLoadError("You must be logged in to edit your custom workflow.");
    }

    return () => {
      cancelled = true;
    };
  }, [jwt, user]);


  // Load selected terminology / TMX entries so they can be summarized on this screen
  React.useEffect(() => {
    if (!jwt || !localUsesCustom) return;
    loadTerminologyEntries();
    loadTMXEntries();
    // eslint-disable-next-line
  }, [jwt, localUsesCustom]);


  const addPair = () => {
    setHasChanges(true);
    setPairs((prev) => [...prev, { source: "", target: "" }]);
  };

  const removePair = (idx) => {
    setHasChanges(true);
    setPairs((prev) => prev.filter((_, i) => i !== idx));
  };

  const setPairField = (idx, field, value) => {
    setHasChanges(true);
    setPairs((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const toggleFileTypeLabel = (label) => {
    setHasChanges(true);
    setFileTypes((prev) =>
      prev.includes(label)
        ? prev.filter((x) => x !== label)
        : [...prev, label]
    );
  };

  const handleSampleChange = (file) => {
    setHasChanges(true);
    if (!file) {
      setOtherSample({ file: null, filename: "" });
      return;
    }
    setOtherSample({ file, filename: file.name || "" });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveMsg("");

    // Require at least one complete language pair (source and target, not identical)
    const validPairs = (pairs || []).filter((p) => {
      const srcName = normalizeLangValue(p.source || "").trim();
      const tgtName = normalizeLangValue(p.target || "").trim();
      return srcName && tgtName && srcName !== tgtName;
    });

    if (validPairs.length === 0) {
      setSaving(false);
      setSaveError("Please select at least one language pair before saving.");
      return;
    }

    // Prepare pairs_json as structured array of { source, targets: [] }
    const grouped = {};
    validPairs.forEach((p) => {
      const srcName = normalizeLangValue(p.source || "").trim();
      const tgtName = normalizeLangValue(p.target || "").trim();
      if (!srcName || !tgtName) return;
      if (!grouped[srcName]) {
        grouped[srcName] = new Set();
      }
      grouped[srcName].add(tgtName);
    });

    const outgoingPairsArray = Object.entries(grouped).map(
      ([sourceName, targetsSet]) => ({
        source: sourceName,
        targets: Array.from(targetsSet),
      })
    );
    const outgoingPairsJson = JSON.stringify(outgoingPairsArray);

    // Require PEMT and Formatting selections when building a custom workflow
    if (!pemt) {
      setSaving(false);
      setSaveError("Please select a Post-AI Translation Review (PEMT) option.");
      return;
    }
    if (!formatting) {
      setSaving(false);
      setSaveError("Please select a Post-Translation Formatting option.");
      return;
    }

    // Prepare file_types_json as JSON array of labels
    const outgoingFileTypesJson = JSON.stringify(fileTypes || []);

    try {
      const res = await fetch(`${API_BASE}/workflow/draft/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          pairs_json: outgoingPairsJson,
          file_types_json: outgoingFileTypesJson,
          other_file_type: otherFileType || "",
          other_sample_filename: otherSample?.filename || "",
          terminology_enabled: !!terminologyEnabled,
          pemt: pemt || "",
          formatting: formatting || "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.detail || `Save failed (${res.status})`;
        throw new Error(detail);
      }

      // Draft saved successfully
      setSaveMsg("Workflow updated successfully.");
      setHasChanges(false);

      // If user is currently Generic, promote to Custom automatically
      if (!localUsesCustom) {
        try {
          const r = await fetch(`${API_BASE}/workflow/mode`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            },
            credentials: "include",
            body: JSON.stringify({ uses_custom_workflow: true }),
          });
          const modeData = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(modeData?.detail || `Request failed (${r.status})`);
          setLocalUsesCustom(true); // <-- reflect in-place; stay on this screen
          setModeMsg("Switched to Custom Workflow.");
         // Refresh /me right after promoting
          try {
            const me = await fetch(`${API_BASE}/me`, {
              headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
              credentials: "include",
            });
            if (me.ok) {
              const meData = await me.json();
              setUserAndTick && setUserAndTick(meData);
            }
          } catch (_) {}
        } catch (e) {
          console.error("Failed to switch to Custom Workflow:", e);
        }
      }

    } catch (e) {
      setSaveError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };


  const handleAddPair = () => {
    const srcName = normalizeLangValue(newSource).trim();
    const tgtName = normalizeLangValue(newTarget).trim();

    if (!srcName || !tgtName) {
      return;
    }
    if (srcName === tgtName) {
      return;
    }

    setHasChanges(true);
    setPairs((prev) => [...prev, { source: srcName, target: tgtName }]);
    setNewSource("");
    setNewTarget("");
  };

  const newNeedsSource = !newSource && !!newTarget;
  const newNeedsTarget = !!newSource && !newTarget;

  const hasValidPairs = React.useMemo(() => {
    return (pairs || []).some((p) => {
      const srcName = normalizeLangValue(p.source || "").trim();
      const tgtName = normalizeLangValue(p.target || "").trim();
      return srcName && tgtName && srcName !== tgtName;
    });
  }, [pairs, normalizeLangValue]);

  const standardFileTypes = React.useMemo(
    () => FILE_TYPES.filter((label) => label !== OTHER_LABEL),
    []
  );

  const allStandardFileTypesSelected = React.useMemo(
    () => standardFileTypes.every((label) => fileTypes.includes(label)),
    [standardFileTypes, fileTypes]
  );

  const handleToggleAllStandardFileTypes = () => {
    setHasChanges(true);
    setFileTypes((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const currentlyAllSelected = standardFileTypes.every((label) =>
        current.includes(label)
      );

      if (currentlyAllSelected) {
        return current.filter((label) => label === OTHER_LABEL);
      }

      return Array.from(new Set([...current, ...standardFileTypes]));
    });
  };


  // Group terminology entries by display source/target name
  const groupedTerminology = React.useMemo(() => {
    if (!Array.isArray(terminologyEntries) || terminologyEntries.length === 0) return {};
    return terminologyEntries.reduce((acc, e) => {
      const key = `${displayLang(e.source_code || "")} → ${displayLang(e.target_code || "")}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(e);
      return acc;
    }, {});
  }, [terminologyEntries, displayLang]);

  // Group TMX entries by display source/target name
  const groupedTMX = React.useMemo(() => {
    if (!Array.isArray(tmxEntries) || tmxEntries.length === 0) return {};
    return tmxEntries.reduce((acc, e) => {
      const key = `${displayLang(e.source_code || "")} → ${displayLang(e.target_code || "")}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(e);
      return acc;
    }, {});
  }, [tmxEntries, displayLang]);


  const otherSelected = fileTypes.includes(OTHER_LABEL);
  const otherEmpty = otherSelected && !(otherFileType || "").trim().length;

  // Warn if the user tries to close or reload the page with unsaved changes
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleBeforeUnload = (e) => {
      if (!hasChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  const handleOpenTerminology = () => {
    if (onOpenTerminologySettings) {
      onOpenTerminologySettings();
    }
  };

  const handleOpenDataUsage = () => {
    if (onOpenTMXSettings) {
      onOpenTMXSettings();
    }
  };

  // --- Workflow mode toggle (mirror Settings) ---
  const [modeMsg, setModeMsg] = React.useState("");
  const [modeErr, setModeErr] = React.useState("");
  const [modePending, setModePending] = React.useState(false);

  const postWorkflowMode = async (flag) => {
    try {
      setModeMsg("");
      setModeErr("");
      setModePending(true);
      const r = await fetch(`${API_BASE}/workflow/mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ uses_custom_workflow: !!flag }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
      setModeMsg(flag ? "Switched to Custom Workflow." : "Switched to Generic Workflow.");
      setLocalUsesCustom(!!flag);

      // NEW: refresh /me so App sees the updated uses_custom_workflow immediately
      try {
        const me = await fetch(`${API_BASE}/me`, {
          headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
          credentials: "include",
        });
        if (me.ok) {
          const meData = await me.json();
          setUserAndTick && setUserAndTick(meData);
        }
      } catch (_) {}
    } catch (e) {
      setModeErr(String(e.message || e));
    } finally {
      setModePending(false);
    }
  };

  const handleSwitchToCustom = () => postWorkflowMode(true);
  const handleSwitchToGeneric = () => postWorkflowMode(false);


  return (
    <div data-testid="custom-workflow-page" className="max-w-5xl mx-auto mt-8 px-4 pb-10">
      {onBack && (
        <button
          type="button"
          onClick={() => {
            if (hasChanges) {
              const leave = window.confirm(
                "You have unsaved changes. If you leave now, your changes will be discarded. Do you want to leave without saving?"
              );
              if (!leave) {
                return;
              }
            }
            onBack();
          }}
          className="mb-4 text-sm underline font-bold"
          style={{ color: "#00a7e1" }}
        >
          ← Back to Settings
        </button>
      )}

      <h2 data-testid="custom-workflow-title" className="text-xl font-semibold">Custom Workflow Settings</h2>

      {/* Current mode & quick toggle + top Save/Cancel */}
      <div className="mt-2 mb-2 flex flex-wrap items-center gap-3 text-sm">

        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={handleSwitchToCustom}
            disabled={modePending || !!localUsesCustom}
            className={`px-2 py-1 rounded-full text-xs font-semibold border transition ${
              localUsesCustom ? "text-white shadow-sm cursor-default" : "bg-white hover:opacity-90"
            } ${modePending ? "opacity-70" : ""}`}
            style={
              localUsesCustom
                ? { backgroundColor: "#00a7e1", borderColor: "#00a7e1" }
                : { color: "#00a7e1", borderColor: "#00a7e1" }
            }
            aria-pressed={!!localUsesCustom}
          >
            Custom Workflow
          </button>

          <button
            type="button"
            onClick={() => {
              if (modePending) return;
              localUsesCustom ? handleSwitchToGeneric() : handleSwitchToCustom();
            }}
            disabled={modePending}
            className={`inline-flex items-center justify-center border ${
              modePending ? "cursor-default opacity-70" : "cursor-pointer"
            }`}
            style={{
              backgroundColor: "#e5e7eb",
              width: "34px",
              minWidth: "34px",
              height: "18px",
              borderRadius: "9999px",
              position: "relative",
              transition: "background-color 0.2s ease",
            }}
            aria-label={`Switch to ${localUsesCustom ? "Generic Workflow" : "Custom Workflow"}`}
            title={`Switch to ${localUsesCustom ? "Generic Workflow" : "Custom Workflow"}`}
          >
            <span
              style={{
                position: "absolute",
                top: "1px",
                left: localUsesCustom ? "1px" : "17px",
                width: "14px",
                height: "14px",
                borderRadius: "9999px",
                backgroundColor: "#00a7e1",
                transition: "left 0.2s ease",
              }}
            />
          </button>

          <button
            type="button"
            onClick={handleSwitchToGeneric}
            disabled={modePending || !localUsesCustom}
            className={`px-2 py-1 rounded-full text-xs font-semibold border transition ${
              !localUsesCustom ? "text-white shadow-sm cursor-default" : "bg-white hover:opacity-90"
            } ${modePending ? "opacity-70" : ""}`}
            style={
              !localUsesCustom
                ? { backgroundColor: "#00a7e1", borderColor: "#00a7e1" }
                : { color: "#00a7e1", borderColor: "#00a7e1" }
            }
            aria-pressed={!localUsesCustom}
          >
            Generic Workflow
          </button>
        </div>

        {(modeMsg || modeErr) && (
          <span className="text-xs">
            {modeMsg && <span style={{ color: "#00a7e1" }}>{modeMsg}</span>}
            {modeErr && <span className="text-red-600">{modeErr}</span>}
          </span>
        )}

        {/* Right-aligned top Save/Cancel (mirrors bottom buttons) */}
        {localUsesCustom && (
          <div className="ml-auto flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <button
                data-testid="custom-workflow-save"
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={`text-white px-4 py-2 rounded text-sm ${
                  saving ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
                }`}
                style={{ backgroundColor: "#00a7e1" }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (hasChanges) {
                    const leave = window.confirm(
                      "You have unsaved changes. If you leave now, your changes will be discarded. Do you want to leave without saving?"
                    );
                    if (!leave) {
                      return;
                    }
                  }
                  if (onBack) {
                    onBack();
                  }
                }}
                className="px-4 py-2 rounded border text-sm hover:opacity-90"
              >
                Cancel
              </button>
            </div>
            {!saveMsg && saveError && (
              <div data-testid="custom-workflow-save-error" className="text-xs text-red-600 text-right">{saveError}</div>
            )}
          </div>
        )}
      </div>


      {loading ? (
        <div className="text-sm text-gray-700">Loading workflow…</div>
      ) : loadError ? (
        <div data-testid="custom-workflow-load-error" className="text-sm text-red-600">{loadError}</div>
      ) : (
        localUsesCustom ? (
		<>
		  {/* Language pairs */}
          <div data-testid="custom-workflow-language-pairs" className="border rounded p-4 space-y-3 mt-4">
            <h3 className="font-semibold">Language Pairs</h3>
            <p className="text-xs text-gray-600">
              Update the source and target language pairs used in your custom
              workflow.
            </p>

            {/* Existing committed language pairs (read-only) */}
            {pairs.length > 0 && (
              <div className="space-y-2 mt-2">
                {pairs.map((p, idx) => {
                  const sourceValue = normalizeLangValue(p.source);
                  const targetValue = normalizeLangValue(p.target);
                  if (!sourceValue || !targetValue) return null;

                  return (
                    <div
                      key={`${sourceValue}-${targetValue}-${idx}`}
                      className="flex items-center gap-3 text-sm text-gray-800"
                    >
                      <span>
                        {sourceValue} {"\u2192"} {targetValue}
                      </span>
                      <button
                        data-testid="custom-workflow-remove-language-pair"
                        type="button"
                        onClick={() => removePair(idx)}
                        className="rounded-xl border border-gray-300 px-2 py-1 text-xs md:text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* New language pair row */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">
                  Source Language
                </label>
                <select
                  data-testid="custom-workflow-source-language"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  value={newSource}
                  onChange={(e) => {
                    setHasChanges(true);
                    setNewSource(e.target.value);
                  }}
                >
                  <option value="">Select source…</option>
                  {languageList.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {newNeedsSource && (
                  <div
                    className="text-xs text-red-700 mt-1"
                    aria-live="polite"
                  >
                    Please select a source language.
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">
                  Target Language
                </label>
                <select
                  data-testid="custom-workflow-target-language"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  value={newTarget}
                  onChange={(e) => {
                    setHasChanges(true);
                    setNewTarget(e.target.value);
                  }}
                >
                  <option value="">Select target…</option>
                  {languageList
                    .filter((name) => name !== newSource)
                    .map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                </select>
                {newNeedsTarget && (
                  <div
                    className="text-xs text-red-700 mt-1"
                    aria-live="polite"
                  >
                    Please select a target language.
                  </div>
                )}
              </div>

              <div className="md:col-span-1 flex justify-end md:justify-start mt-6">
                <button
                  data-testid="custom-workflow-add-language-pair"
                  type="button"
                  onClick={handleAddPair}
                  disabled={
                    !newSource ||
                    !newTarget ||
                    newSource === newTarget
                  }
                  className="rounded-xl px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: "#00a7e1" }}
                >
                  Add
                </button>
              </div>
            </div>

            {!hasValidPairs && (
              <p data-testid="custom-workflow-language-pair-required" className="text-xs text-red-700 mt-2">
                Please select at least one language pair (source and target).
              </p>
            )}
          </div>

          {/* File types */}
          <div data-testid="custom-workflow-file-types" className="border rounded p-4 space-y-3 mt-4">
            <h3 className="font-semibold">File Types</h3>
            <p className="text-xs text-gray-600">
              Select the file types you plan to use in this workflow, including
              any custom types.
            </p>

            <button
              data-testid="custom-workflow-toggle-all-file-types"
              type="button"
              onClick={handleToggleAllStandardFileTypes}
              className="rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "#00a7e1" }}
            >
              {allStandardFileTypesSelected ? "Unselect All" : "Select All"}
            </button>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {FILE_TYPES.filter((label) => label !== OTHER_LABEL).map(
                (label) => (
                  <label
                    key={label}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      data-testid={FILE_TYPE_TEST_IDS[label]}
                      type="checkbox"
                      checked={fileTypes.includes(label)}
                      onChange={() => toggleFileTypeLabel(label)}
                    />
                    {label}
                  </label>
                )
              )}
            </div>

            <div className="mt-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid={FILE_TYPE_TEST_IDS[OTHER_LABEL]}
                  type="checkbox"
                  checked={otherSelected}
                  onChange={() => {
                    setHasChanges(true);
                    setFileTypes((prev) =>
                      otherSelected
                        ? prev.filter((x) => x !== OTHER_LABEL)
                        : [...prev, OTHER_LABEL]
                    );
                  }}
                />

                {OTHER_LABEL}
              </label>
            </div>

            {otherSelected && (
              <div className="mt-3">
                <label className="block text-sm text-gray-700 mb-1">
                  Please specify file type and describe file requirements:
                </label>
                <textarea
                  data-testid="custom-workflow-other-file-type-description"
                  value={otherFileType}
                  onChange={(e) => {
                    setHasChanges(true);
                    setOtherFileType(e.target.value);
                  }}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm resize-y overflow-y-auto"
                  placeholder="e.g., Markdown, DITA, LaTeX, custom XML, etc."
                  rows={4}
                  maxLength={250}
                />
                <div className="mt-1 text-xs text-gray-500">
                  {otherFileType.length}/250 characters
                </div>
                {otherEmpty && (
                  <p
                    data-testid="custom-workflow-other-file-type-required"
                    className="text-xs text-red-700 mt-1"
                    aria-live="polite"
                  >
                    Please enter custom file type and description.
                  </p>
                )}

                <div className="mt-4">
                  <label className="block text-sm text-gray-700 mb-1">
                    Please select a sample file if available (optional)
                  </label>
                  <button
                    data-testid="custom-workflow-choose-sample-file"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                    style={{ backgroundColor: "#00a7e1" }}
                  >
                    Choose Sample File
                  </button>
                  <input
                    data-testid="custom-workflow-sample-file-input"
                    ref={fileInputRef}
                    type="file"
                    accept=".zip,.txt,.csv,.docx,.xlsx,.pptx,.idml,.html,.xml,.json,.yml,.yaml,.xlf,.xliff,.sdlxliff,.pdf"
                    onChange={(e) =>
                      handleSampleChange(e.target.files?.[0] || null)
                    }
                    className="hidden"
                  />
                  {otherSample?.filename && (
                    <div className="mt-2 flex items-center gap-3 text-sm text-gray-700">
                      <span>
                        Selected:{" "}
                        <span className="font-semibold">
                          {otherSample.filename}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setHasChanges(true);
                          setOtherSample({ file: null, filename: "" });
                        }}
                        className="text-sm text-red-600 underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {fileTypes.length === 0 && (
              <p data-testid="custom-workflow-file-type-required" className="text-xs text-red-700 mt-2">
                Please select at least one file type to keep your workflow active.
              </p>
            )}
          </div>

          {/* Terminology enforcement summary */}
          <div data-testid="custom-workflow-terminology" className="border rounded p-4 space-y-3 mt-4">
            <h3 className="font-semibold">Terminology Enforcement</h3>
            <p className="text-xs text-gray-600">
              Choose whether terminology files are applied to your translations 
			  for your language pairs.
            </p>

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                data-testid="custom-workflow-terminology-enabled"
                type="checkbox"
                className="form-checkbox"
                checked={terminologyEnabled}
                disabled={!hasValidPairs}
                onChange={(e) => {
                  setHasChanges(true);
                  setTerminologyEnabled(e.target.checked);
                }}
              />
              <span className={!hasValidPairs ? "text-gray-500" : ""}>
                Enable terminology enforcement for this workflow
              </span>
            </label>

            <div className="text-xs text-gray-700">
              Status:{" "}
              <span data-testid="custom-workflow-terminology-status" className="font-semibold">
                {terminologyEnabled ? "On" : "Off"}
              </span>
            </div>

            {terminologyEnabled && (
              <>
                <button
                  data-testid="custom-workflow-choose-terminology-files"
                  type="button"
                  onClick={handleOpenTerminology}
                  className="mt-2 text-white px-4 py-2 rounded text-sm hover:opacity-90"
                  style={{ backgroundColor: "#00a7e1" }}
                >
                  Choose Terminology Files
                </button>

                <div className="mt-2">
                  <div className="text-sm font-semibold mb-2">Terminology Files Selected</div>

                  {terminologyLoading ? (
                    <p className="text-sm text-gray-600">Loading terminology files…</p>
                  ) : terminologyEntries.length === 0 ? (
                    <p data-testid="custom-workflow-no-terminology-files" className="text-sm text-gray-600">
                      No terminology files selected yet.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(groupedTerminology).map(([pair, rows]) => (
                        <div key={pair} className="border border-gray-100 rounded-lg p-3">
                          <div className="text-sm font-semibold mb-2">{pair}</div>
                          <div className="space-y-1">
                            {rows.map((e) => (
                              <div
                                key={e.id || `${pair}-${e.filename}`}
                                className="text-xs text-gray-700"
                              >
                                <span className="font-semibold">File:</span>{" "}
                                {e.filename || "(no filename)"}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>


              </>
            )}

            {!hasValidPairs && (
              <p data-testid="custom-workflow-terminology-language-pair-required" className="text-xs text-red-700 mt-2">
                Please select at least one language pair.
              </p>
            )}
          </div>

          {/* Data usage / TMX summary */}
          <div data-testid="custom-workflow-tmx" className="border rounded p-4 space-y-3 mt-4">
            <h3 className="font-semibold">Data Usage / LLM Translation Training (via TMX)</h3>
            <p className="text-xs text-gray-600">
              Choose whether your previous translations corpora are used for
              training the translations for your language pairs. Only TMX
              files are allowed.
            </p>

            {/* Data Sharing (moved here from Settings.jsx) */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Data Sharing</h4>
              <div className="space-y-2 text-sm text-gray-700">
                <label className="flex items-center gap-2">
                  <input
                    data-testid="custom-workflow-no-share-data"
                    type="radio"
                    name="customworkflow_share_mode"
                    defaultChecked
                  />
                  <span>Do not share my data or use it for training other models</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    data-testid="custom-workflow-public-share-data"
                    type="radio"
                    name="customworkflow_share_mode"
                  />
                  <span>My data can be shared publicly</span>
                </label>
              </div>
              <p className="mt-3 text-xs text-gray-600">
                Even with siloed data, we strongly encourage you to <b>not</b> share sensitive information.
                If you need a concrete siloed architecture, please contact us at{" "}
                <a
                  href="mailto:support@zabtranslation.com"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                >
                  support@zabtranslation.com
                </a>.
              </p>
            </div>

            <button
              data-testid="custom-workflow-choose-tmx-files"
              type="button"
              onClick={handleOpenDataUsage}
              disabled={!hasValidPairs}
              className={`mt-2 text-white px-4 py-2 rounded text-sm ${
                !hasValidPairs ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
              }`}
              style={{ backgroundColor: "#00a7e1" }}
            >
              Choose TMX Files
            </button>

            {!hasValidPairs && (
              <p data-testid="custom-workflow-tmx-language-pair-required" className="text-xs text-red-700 mt-2">
                Please select at least one language pair.
              </p>
            )}

            <div className="mt-2">
              <div className="text-sm font-semibold mb-2">TMX Files Selected</div>

              {tmxLoading ? (
                <p className="text-sm text-gray-600">Loading TMX files…</p>
              ) : tmxEntries.length === 0 ? (
                <p data-testid="custom-workflow-no-tmx-files" className="text-sm text-gray-600">No TMX files selected yet.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(groupedTMX).map(([pair, rows]) => (
                    <div key={pair} className="border border-gray-100 rounded-lg p-3">
                      <div className="text-sm font-semibold mb-2">{pair}</div>
                      <div className="space-y-1">
                        {rows.map((e) => (
                          <div
                            key={e.id || `${pair}-${e.filename}`}
                            className="text-xs text-gray-700"
                          >
                            <span className="font-semibold">File:</span>{" "}
                            {e.filename || "(no filename)"}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>


          </div>

          {/* PEMT (Post-AI Translation Review) */}
          <div data-testid="custom-workflow-pemt" className="border rounded p-4 space-y-3 mt-4">
            <h3 className="font-semibold">
              Post-AI Translation Review (PEMT)
            </h3>
            <p className="text-xs text-gray-600">
              Choose how much human review you want after the AI translation.
            </p>
            <div className="space-y-2 text-sm">
              {PEMT_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    data-testid={`custom-workflow-pemt-${opt.key}`}
                    type="radio"
                    name="pemt"
                    checked={pemt === opt.key}
                    onChange={() => {
                      setHasChanges(true);
                      setPemt(opt.key);
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Post-translation formatting */}
          <div data-testid="custom-workflow-post-formatting" className="border rounded p-4 space-y-3 mt-4">
            <h3 className="font-semibold">Post-Translation Formatting</h3>
            <p className="text-xs text-gray-600">
              Choose how we handle formatting and layout after translation.
            </p>
            <div className="space-y-2 text-sm">
              {PT_FORMATTING.map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    data-testid={`custom-workflow-formatting-${opt.key}`}
                    type="radio"
                    name="formatting"
                    checked={formatting === opt.key}
                    onChange={() => {
                      setHasChanges(true);
                      setFormatting(opt.key);
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
 
          {/* Save status + buttons */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm">
              {saveMsg && (
                <span data-testid="custom-workflow-save-success" className="text-green-700 font-medium">{saveMsg}</span>
              )}
              {!saveMsg && saveError && (
                <span data-testid="custom-workflow-save-error-bottom" className="text-red-600">{saveError}</span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={`text-white px-4 py-2 rounded text-sm ${
                  saving ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
                }`}
                style={{ backgroundColor: "#00a7e1" }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (hasChanges) {
                    const leave = window.confirm(
                      "You have unsaved changes. If you leave now, your changes will be discarded. Do you want to leave without saving?"
                    );
                    if (!leave) {
                      return;
                    }
                  }
                  if (onBack) {
                    onBack();
                  }
                }}
                className="px-4 py-2 rounded border text-sm hover:opacity-90"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
		) : (
          <div className="text-sm text-gray-700 mt-2">
            You are currently using the <b>Generic Workflow</b>. Click
            <span className="font-semibold"> Custom Workflow</span> above to configure custom settings.
          </div>
		)
      )}
    </div>
  );
}